import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';
import { StandardEvents } from '@shopify/events';
import '@theme/product-form';

const OPTION_SEPARATOR = '\u001f';
// Only structural edits require a resync; price/notice text updates do not.
const BUNDLE_STRUCTURE_SELECTOR =
  '[data-bundle-variants], [data-bundle-upsell-variants], [data-bundle-tier], [data-bundle-tier-media], [data-bundle-unit], ' +
  '[data-bundle-tier-radio], [data-bundle-upsell], [data-bundle-upsell-check], ' +
  '[data-bundle-option-select], [data-bundle-option-swatch], [data-bundle-buy-buttons], ' +
  '[data-bundle-buy-now], [data-bundle-size-guide-trigger], [data-bundle-subscriptions], [data-bundle-savings-template], ' +
  '[data-bundle-purchase-option], [data-bundle-selling-plan], product-form-component, shipping-notice';

class VariantCatalog {
  /** @param {{optionCount?: number, variants?: Array<any>, requiresSellingPlan?: boolean, sellingPlans?: Array<any>, tierImageFallbackUrl?: string}} data */
  constructor(data = {}) {
    this.optionCount = Number(data.optionCount) || 0;
    this.variants = Array.isArray(data.variants) ? data.variants : [];
    this.tierImageFallbackUrl = data.tierImageFallbackUrl || '';
    this.requiresSellingPlan = data.requiresSellingPlan === true;
    this.sellingPlans = Array.isArray(data.sellingPlans)
      ? data.sellingPlans.filter((plan) => plan?.id != null && plan.recurringDeliveries === true)
      : [];
    this.variantByKey = new Map();
    this.availableValues = new Map();

    for (const variant of this.variants) {
      const options = Array.isArray(variant.options) ? variant.options.map(String) : [];
      this.variantByKey.set(this.#key(options), variant);
      if (!variant.available) continue;

      for (let position = 1; position <= this.optionCount; position += 1) {
        const context = options.slice(0, position - 1);
        const indexKey = `${position}:${this.#key(context)}`;
        const values = this.availableValues.get(indexKey) || new Set();
        values.add(options[position - 1]);
        this.availableValues.set(indexKey, values);
      }
    }
  }

  /** @param {string[]} options */
  #key(options) {
    return options.join(OPTION_SEPARATOR);
  }

  /** @param {Record<number, string>} selection */
  resolve(selection) {
    if (this.variants.length === 1 && Object.keys(selection).length === 0) return this.variants[0];

    const options = [];
    for (let position = 1; position <= this.optionCount; position += 1) {
      const value = selection[position];
      if (value == null) return null;
      options.push(String(value));
    }
    return this.variantByKey.get(this.#key(options)) || null;
  }

  /**
   * @param {Record<number, string>} selection
   * @param {number} position
   * @param {string} candidate
   */
  isCandidateAvailable(selection, position, candidate) {
    const context = [];
    for (let index = 1; index < position; index += 1) {
      const value = selection[index];
      if (value == null) return false;
      context.push(String(value));
    }

    const values = this.availableValues.get(`${position}:${this.#key(context)}`);
    return values ? values.has(candidate) : false;
  }

  /**
   * Keep valid choices and repair following options after a parent option changes.
   * This makes every available combination reachable without submitting an invalid one.
   * @param {Record<number, string>} selection
   * @returns {Record<number, string> | null}
   */
  reconcile(selection) {
    const resolved = { ...selection };
    const prefix = [];
    for (let position = 1; position <= this.optionCount; position += 1) {
      const values = this.availableValues.get(`${position}:${this.#key(prefix)}`);
      if (!values?.size) return null;
      const value = String(resolved[position] ?? '');
      resolved[position] = values.has(value) ? value : values.values().next().value;
      prefix.push(resolved[position]);
    }
    return this.resolve(resolved)?.available ? resolved : null;
  }
}

/**
 * Bundle quantity selector built as a Horizon theme block.
 * Owns only bundle-specific state; cart, buttons, money formatting and media remain native Horizon components.
 */
class BundleQuantity extends Component {
  #abortController = new AbortController();
  #upsellCatalogs = new WeakMap();
  /** @type {VariantCatalog | null} */
  #catalog = null;
  /** @type {Record<number, string>} */
  #rememberedOptions = {};
  /** Last explicitly edited unit per tier, resolved from its current options on every refresh. */
  #tierImageUnits = new Map();
  #tierImageProductId = '';
  /** @type {Promise<boolean> | null} */
  #submissionPromise = null;
  /**
   * Confirmed Buy now payload for navigation retries in this document only.
   * @type {{signature: string} | null}
   */
  #confirmedCheckout = null;
  /** @type {number | null} */
  #historyRefreshFrame = null;
  #subscriptionConfiguration = '';
  #purchaseOption = 'one-time';
  #sellingPlanId = '';
  /** @type {Map<HTMLElement, Animation>} */
  #subscriptionAnimations = new Map();
  #moneyFormatterKey = '';
  /** @type {((cents: number) => string) | null} */
  #moneyFormatter = null;

  #catalogText = '';
  #refreshScheduled = false;
  #contentObserver = new MutationObserver((mutations) => {
    const changed = mutations.some((mutation) => {
      if (mutation.type === 'attributes') {
        return mutation.target === this || (mutation.target instanceof Element && mutation.target.matches(
          '[data-bundle-subscriptions], [data-bundle-subscription-first-order], [data-bundle-subscription-plan-name], ' +
          '[data-bundle-subscription-quantity], [data-bundle-subscription-renewal], [data-bundle-savings-template], [data-bundle-upsell], [data-bundle-tier]'
        ));
      }
      if (mutation.target instanceof Element &&
          mutation.target.matches('[data-bundle-variants], [data-bundle-upsell-variants], [data-bundle-option-select], [data-bundle-selling-plan]')) {
        return true;
      }
      return [...mutation.addedNodes, ...mutation.removedNodes].some((node) =>
        node instanceof Element && (node.matches(BUNDLE_STRUCTURE_SELECTOR) || node.querySelector(BUNDLE_STRUCTURE_SELECTOR))
      );
    });
    if (changed) this.#scheduleRefresh();
  });

  connectedCallback() {
    super.connectedCallback();

    // Bind even if an editor insertion has not supplied the children yet.
    if (this.#abortController.signal.aborted) this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    this.addEventListener('change', this.#handleChange, { signal });
    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('submit', this.#handleSubmit, { signal });
    this.addEventListener('animationend', this.#handleAnimationEnd, { signal });
    window.addEventListener('pageshow', this.#handlePageShow, { signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, { signal });
    this.#contentObserver.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: [
        'data-size-guide-enabled', 'data-size-guide-label', 'data-size-option-names',
        'data-subscription-mode', 'data-subscription-default',
        'data-bundle-show-selector', 'data-bundle-show-delivery-selector', 'data-bundle-rich-text',
        'data-template', 'data-no-gifts-template', 'data-first-payment-template', 'data-payments-template',
        'data-bundle-unit-singular', 'data-bundle-unit-plural',
        'data-bundle-savings-template', 'data-money-sample', 'data-money-with-currency-sample',
        'data-currency', 'data-show-currency', 'data-locale',
        'data-upsell-tier-visibility', 'data-upsell-tier-positions',
        'data-upsell-quantity', 'data-merge-visible-upsells', 'data-show-unit-numbers',
        'data-bundle-image-behavior', 'data-product-id',
      ],
    });
    this.#refreshFromDOM();
    this.#handlePageShow();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#scheduleRefresh();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#contentObserver.disconnect();
    if (this.#historyRefreshFrame !== null) cancelAnimationFrame(this.#historyRefreshFrame);
    this.#historyRefreshFrame = null;
    for (const animation of this.#subscriptionAnimations.values()) animation.cancel();
    this.#subscriptionAnimations.clear();
  }

  /** @param {PageTransitionEvent} [event] */
  #handlePageShow = (event) => {
    const navigation = /** @type {PerformanceNavigationTiming | undefined} */ (
      performance.getEntriesByType('navigation')[0]
    );
    if (!event?.persisted && navigation?.type !== 'back_forward') return;
    // Checkout may have changed the cart before a cached document returns.
    // A new document already starts without a receipt; an early click before
    // its initial pageshow must keep any confirmation it has just received.
    if (event?.persisted) this.#confirmedCheckout = null;
    if (this.#historyRefreshFrame !== null) cancelAnimationFrame(this.#historyRefreshFrame);
    // The browser restores form controls during history traversal. Reconcile
    // their final values on the next frame, as the native sticky does on show.
    this.#historyRefreshFrame = requestAnimationFrame(() => {
      this.#historyRefreshFrame = null;
      if (this.isConnected) this.#refreshFromDOM();
    });
  };

  /**
   * A confirmed cart edit ends the old checkout retry, including drawer edits.
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    const confirmed = this.#confirmedCheckout;
    if (!confirmed || !event.promise) return;
    event.promise.then(({ detail }) => {
      // An older event must not erase a newer successful checkout addition.
      if (detail?.didError === false && this.#confirmedCheckout === confirmed) {
        this.#confirmedCheckout = null;
      }
    }).catch(() => {
      // No confirmed change: keep the existing navigation retry available.
    });
  };

  #scheduleRefresh() {
    if (this.#refreshScheduled) return;
    this.#refreshScheduled = true;
    queueMicrotask(() => {
      this.#refreshScheduled = false;
      if (this.isConnected) this.#refreshFromDOM();
    });
  }

  #refreshFromDOM() {
    const productId = this.dataset.productId || '';
    if (productId !== this.#tierImageProductId) {
      this.#tierImageProductId = productId;
      this.#tierImageUnits.clear();
    }
    const source = this.querySelector('[data-bundle-variants]');
    const text = source?.textContent || '';
    if (!this.#catalog || text !== this.#catalogText) {
      this.#catalog = this.#parseCatalog(source);
      this.#catalogText = text;
      this.#rememberedOptions = {};
    }
    this.#configureSizeGuideTriggers();
    if (!this.#catalog) {
      this.#updateButtonState();
      return;
    }

    // new-horizon: fetched quick-add/cart-edit HTML does not execute the notice's module script.
    if (this.querySelector('shipping-notice') && !customElements.get('shipping-notice')) {
      import('@theme/shipping-notice').catch((error) =>
        console.warn('[bundle-quantity] Shipping notice failed to load', error)
      );
    }

    this.#upsellCatalogs = new WeakMap();
    this.#normalizeTierGroup();
    this.#initializeSubscriptions();
    this.#initializeUpsells();
    // Inactive tiers also need their image reconciled after an editor render.
    for (const tier of this.querySelectorAll('[data-bundle-tier]')) this.#refreshTierImage(tier);
    this.#refreshAll();
  }

  /** @param {HTMLScriptElement | Element | null} element */
  #parseCatalog(element) {
    if (!(element instanceof HTMLScriptElement)) return null;
    try {
      return new VariantCatalog(JSON.parse(element.textContent || '{}'));
    } catch (error) {
      console.warn('[bundle-quantity] Invalid variant data', error);
      return null;
    }
  }

  #normalizeTierGroup() {
    const radios = Array.from(this.querySelectorAll('[data-bundle-tier-radio]'));
    const tierKeys = new Set(radios.map((radio) => radio.getAttribute('value')));
    for (const key of this.#tierImageUnits.keys()) {
      if (!tierKeys.has(key)) this.#tierImageUnits.delete(key);
    }
    if (radios.length === 0) return;

    const groupName = `${this.id}-tier`;
    radios.forEach((radio) => {
      if (radio instanceof HTMLInputElement) radio.name = groupName;
    });

    const checked = radios.filter((radio) => radio instanceof HTMLInputElement && radio.checked);
    const winner = checked[0] || radios[0];
    radios.forEach((radio) => {
      if (radio instanceof HTMLInputElement) radio.checked = radio === winner;
    });

    const tier = winner instanceof Element ? winner.closest('[data-bundle-tier]') : null;
    if (tier instanceof HTMLElement) this.#activateTier(tier, false);
  }

  #initializeUpsells() {
    for (const upsell of this.querySelectorAll('[data-bundle-upsell]')) {
      if (!(upsell instanceof HTMLElement)) continue;
      if (upsell.closest('bundle-quantity') !== this) continue;
      upsell.hidden = !this.#upsellAppliesToTier(upsell);
      if (upsell.hidden) continue;
      this.#refreshUpsell(upsell);
    }
    this.#syncUpsellGroups();
  }

  /** Group existing blocks without cloning controls or changing their settings. */
  #syncUpsellGroups() {
    const container = this.querySelector('.bundle-quantity__items');
    if (!(container instanceof HTMLElement)) return;
    let groups = Array.from(container.children).filter((node) => node.hasAttribute('data-bundle-upsell-group'));
    const unwrap = (group) => {
      for (const child of Array.from(group.children)) {
        child.removeAttribute('data-upsell-merged');
        child.removeAttribute('data-upsell-divider');
        container.insertBefore(child, group);
      }
      group.remove();
    };

    // Editor insertions may temporarily place a different block inside a group.
    // Restore its exact order before calculating the new runs.
    if (groups.some((group) => Array.from(group.children).some((child) => !child.matches('[data-bundle-upsell]')))) {
      groups.forEach(unwrap);
      groups = [];
    }

    if (this.dataset.mergeVisibleUpsells !== 'true') {
      groups.forEach(unwrap);
      return;
    }

    // Flatten only our own presentation wrappers when calculating order. Other
    // blocks interrupt a run, so a custom block/button never changes position.
    const entries = Array.from(container.children).flatMap((node) =>
      node.hasAttribute('data-bundle-upsell-group') ? Array.from(node.children) : [node]
    );
    const runs = [];
    let run = [];
    for (const node of entries) {
      if (node.matches('[data-bundle-upsell]') && node.closest('bundle-quantity') === this) {
        run.push(node);
      } else {
        if (run.length) runs.push(run);
        run = [];
      }
    }
    if (run.length) runs.push(run);

    const used = new Set();
    const presentationAttributes = [
      'data-upsell-style', 'data-upsell-badge-style', 'data-upsell-badge-animated',
      'data-upsell-custom-border', 'data-upsell-custom-image-size', 'data-upsell-selected-background',
    ];

    for (const members of runs) {
      const first = members[0];
      let group = first.parentElement;
      const reuse = group?.hasAttribute('data-bundle-upsell-group') && !used.has(group) &&
        group.childElementCount === members.length && members.every((member) => member.parentElement === group);
      if (!reuse) {
        const anchor = first.parentElement?.hasAttribute('data-bundle-upsell-group') ? first.parentElement : first;
        group = document.createElement('div');
        group.className = 'bundle-upsell bundle-upsell-group';
        group.setAttribute('data-bundle-upsell-group', '');
        container.insertBefore(group, anchor);
        for (const member of members) group.append(member);
      }
      used.add(group);

      const visible = members.filter((member) => !member.hidden);
      group.hidden = visible.length === 0;
      for (const member of members) {
        member.setAttribute('data-upsell-merged', '');
        member.toggleAttribute('data-upsell-divider', !member.hidden && member !== visible[0]);
      }

      const source = visible[0];
      if (!source) continue;
      const style = source.getAttribute('style') || '';
      if (group.getAttribute('style') !== style) group.setAttribute('style', style);
      for (const name of presentationAttributes) {
        const value = source.getAttribute(name);
        if (value === null) group.removeAttribute(name);
        else if (group.getAttribute(name) !== value) group.setAttribute(name, value);
      }
    }

    for (const group of groups) {
      if (!used.has(group)) group.remove();
    }
  }

  /** @param {HTMLElement} upsell */
  #upsellAppliesToTier(upsell) {
    if (upsell.closest('bundle-quantity') !== this) return false;
    if (upsell.dataset.upsellTierVisibility !== 'selected') return true;

    // Count only actual tiers in this bundle, including tiers with free units.
    const tiers = Array.from(this.querySelectorAll('[data-bundle-tier]')).filter((tier) =>
      tier.closest('bundle-quantity') === this
    );
    const position = tiers.findIndex((tier) => tier.querySelector('[data-bundle-tier-radio]:checked')) + 1;
    if (position === 0) return false;

    return (upsell.dataset.upsellTierPositions || '').split(',').some((value) =>
      /^\d+$/.test(value.trim()) && Number(value) === position
    );
  }

  /** @param {HTMLElement} upsell */
  #getUpsellCatalog(upsell) {
    const cached = this.#upsellCatalogs.get(upsell);
    if (cached instanceof VariantCatalog) return cached;
    const catalog = this.#parseCatalog(upsell.querySelector('[data-bundle-upsell-variants]'));
    if (catalog) this.#upsellCatalogs.set(upsell, catalog);
    return catalog;
  }

  /** @param {Event} event */
  #handleClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.closest('[data-bundle-size-guide-trigger]')) {
      this.#openSizeGuide();
      return;
    }

    if (target.closest('[data-bundle-buy-now]')) {
      this.#buyNow();
      return;
    }

    const tier = target.closest('[data-bundle-tier]');
    if (!(tier instanceof HTMLElement)) return;
    if (target.closest('select, input, button, a, [data-bundle-variant-area]')) return;

    const radio = tier.querySelector('[data-bundle-tier-radio]');
    if (!(radio instanceof HTMLInputElement) || radio.checked) return;
    radio.checked = true;
    radio.dispatchEvent(new Event('change', { bubbles: true }));
  };

  /** @param {SubmitEvent} event */
  #handleSubmit = (event) => {
    if (!(event.target instanceof HTMLFormElement) || !event.target.closest('bundle-quantity')) return;
    event.preventDefault();
    this.#addToCart();
  };

  /** @param {Event} event */
  #handleChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;

    if (target.matches('[data-bundle-purchase-option]')) {
      if (this.#submissionPromise) return;
      const wasSubscription = this.#hasActiveSubscription();
      this.#purchaseOption = target.value === 'subscription' ? 'subscription' : 'one-time';
      this.#refreshAll();
      if (wasSubscription !== this.#hasActiveSubscription()) this.#animateSubscriptionDetails();
      return;
    }

    if (target.matches('[data-bundle-selling-plan]')) {
      if (this.#submissionPromise) return;
      this.#sellingPlanId = target.value;
      this.#refreshAll();
      return;
    }

    if (target.matches('[data-bundle-tier-radio]')) {
      const tier = target.closest('[data-bundle-tier]');
      if (tier instanceof HTMLElement) this.#activateTier(tier, true);
      return;
    }

    if (target.matches('[data-bundle-upsell-check]')) {
      const upsell = target.closest('[data-bundle-upsell]');
      if (upsell instanceof HTMLElement) {
        this.#refreshUpsell(upsell);
        this.#updateButtonState();
      }
      return;
    }

    if (target.matches('[data-bundle-option-swatch]')) {
      this.#syncSwatchToSelect(target);
    } else if (target.matches('[data-bundle-option-select]')) {
      this.#syncSelectToSwatch(target);
    } else {
      return;
    }

    const unit = target.closest('[data-bundle-unit]');
    if (!(unit instanceof HTMLElement)) return;

    const optionPosition = Number(target.dataset.optionPosition || 0);
    const unitIndex = Number(unit.dataset.bundleUnit || 0);
    const tier = target.closest('[data-bundle-tier]');
    const upsell = target.closest('[data-bundle-upsell]');

    if (tier instanceof HTMLElement) {
      if (this.#catalog) this.#reconcileUnitSelection(unit, this.#catalog);
      if (unitIndex === 1 && optionPosition > 0) {
        const selection = this.#getUnitSelection(unit);
        const value = selection[optionPosition];
        if (value != null) {
          this.#rememberedOptions = { ...selection };
          this.#propagateUnitOne(tier, optionPosition, value);
        }
      }
      // Record the source interaction, not the other units updated by propagation.
      const tierKey = tier.querySelector('[data-bundle-tier-radio]')?.getAttribute('value');
      if (tierKey && unitIndex > 0) this.#tierImageUnits.set(tierKey, unitIndex);
      this.#refreshTier(tier);
      if (tier.matches(':has([data-bundle-tier-radio]:checked)')) this.#syncGallery(tier);
    } else if (upsell instanceof HTMLElement) {
      this.#refreshUpsell(upsell);
    }

    this.#updateButtonState();
  };

  #configureSizeGuideTriggers() {
    const enabled = this.dataset.sizeGuideEnabled === 'true';
    const configuredNames = new Set(
      String(this.dataset.sizeOptionNames || 'Size')
        .split(',')
        .map((name) => name.trim().toLocaleLowerCase())
        .filter(Boolean)
    );
    const hasSizeOption = Array.from(this.querySelectorAll('[data-bundle-tier] [data-option-name]')).some((option) =>
      configuredNames.has(String(option.getAttribute('data-option-name') || '').trim().toLocaleLowerCase())
    );
    const label = this.dataset.sizeGuideLabel || 'Size guide';

    for (const trigger of this.querySelectorAll('[data-bundle-size-guide-trigger]')) {
      if (!(trigger instanceof HTMLButtonElement)) continue;
      trigger.hidden = !(enabled && hasSizeOption);
      const labelElement = trigger.querySelector('[data-bundle-size-guide-label]');
      if (labelElement) labelElement.textContent = label;
    }
  }

  #openSizeGuide() {
    if (this.dataset.sizeGuideEnabled !== 'true') return;
    const id = this.dataset.sizeGuideComponentId;
    if (!id) return;
    const component = document.getElementById(id);
    if (!component) return;

    const show = () => {
      if (typeof component.showDialog === 'function') component.showDialog();
    };

    if (typeof component.showDialog === 'function') {
      show();
      return;
    }

    import('@theme/dialog')
      .then(() => customElements.whenDefined('dialog-component'))
      .then(show)
      .catch((error) => console.warn('[bundle-quantity] Size guide failed to open', error));
  }

  /** @param {AnimationEvent} event */
  #handleAnimationEnd = (event) => {
    if (event.animationName !== 'bundle-option-sync') return;
    if (event.target instanceof Element) {
      event.target.classList.remove('bundle-option--sync');
      event.target.style.removeProperty('--bundle-sync-delay');
    }
  };

  /** @param {HTMLInputElement} input */
  #syncSwatchToSelect(input) {
    const option = input.closest('[data-bundle-option]');
    const select = option?.querySelector('[data-bundle-option-select]');
    if (!(select instanceof HTMLSelectElement)) return;
    select.value = input.value;
    this.#syncSelectToSwatch(select);
  }

  /** @param {HTMLSelectElement} select */
  #syncSelectToSwatch(select) {
    const option = select.closest('[data-bundle-option]');
    if (!option) return;
    for (const input of option.querySelectorAll('[data-bundle-option-swatch]')) {
      if (!(input instanceof HTMLInputElement)) continue;
      const checked = input.value === select.value;
      input.checked = checked;
      input.dataset.currentChecked = String(checked);
    }
  }

  /** @param {HTMLElement} tier @param {boolean} remember */
  #activateTier(tier, remember) {
    for (const candidate of this.querySelectorAll('[data-bundle-tier]')) {
      if (!(candidate instanceof HTMLElement)) continue;
      const active = candidate === tier;
      const radio = candidate.querySelector('[data-bundle-tier-radio]');
      const area = candidate.querySelector('[data-bundle-variant-area]');
      if (radio instanceof HTMLInputElement) radio.checked = active;
      if (area instanceof HTMLElement) {
        const emptySingleVariant = this.#catalog?.variants.length === 1 &&
          !area.querySelector('[data-bundle-option-select], [data-bundle-option-swatch]');
        area.hidden = !active || emptySingleVariant;
      }
    }

    if (remember) this.#applyRememberedOptions(tier);
    this.#refreshTier(tier);
    this.#initializeUpsells();
    // Keep the first product image on page load; selections and modal edits sync media.
    if (remember || this.closest('#quick-add-modal-content')) this.#syncGallery(tier);
    this.#updateButtonState();
  }

  /** @param {HTMLElement} tier */
  #applyRememberedOptions(tier) {
    if (Object.keys(this.#rememberedOptions).length === 0) return;
    for (const unit of tier.querySelectorAll('[data-bundle-unit]')) {
      if (!(unit instanceof HTMLElement)) continue;
      for (const [positionString, value] of Object.entries(this.#rememberedOptions)) {
        this.#setUnitOption(unit, Number(positionString), value, false, 0, false);
      }
    }
  }

  /** @param {HTMLElement} tier @param {number} position @param {string} value */
  #propagateUnitOne(tier, position, value) {
    let sequence = 0;
    for (const unit of tier.querySelectorAll('[data-bundle-unit]')) {
      if (!(unit instanceof HTMLElement) || Number(unit.dataset.bundleUnit) === 1) continue;
      if (this.#setUnitOption(unit, position, value, true, sequence * 70)) sequence += 1;
    }
  }

  /**
   * @param {HTMLElement} unit
   * @param {number} position
   * @param {string} value
   * @param {boolean} flash
   * @param {number} delay
   */
  #setUnitOption(unit, position, value, flash, delay, respectAvailability = true) {
    const option = unit.querySelector(`[data-bundle-option][data-option-position="${position}"]`);
    const select = option?.querySelector('[data-bundle-option-select]');
    if (!(option instanceof HTMLElement) || !(select instanceof HTMLSelectElement)) return false;

    const targetOption = Array.from(select.options).find(
      (candidate) => candidate.value === value && (!respectAvailability || !candidate.disabled)
    );
    if (!targetOption || select.value === value) return false;

    select.value = value;
    this.#syncSelectToSwatch(select);

    if (flash) {
      option.classList.remove('bundle-option--sync');
      option.style.setProperty('--bundle-sync-delay', `${delay}ms`);
      // new-horizon: flush the removed state so rapid changes restart the pseudo-element flash.
      void option.offsetWidth;
      option.classList.add('bundle-option--sync');
    }

    return true;
  }

  /** @param {HTMLElement} unit */
  #getUnitSelection(unit) {
    /** @type {Record<number, string>} */
    const selection = {};
    for (const select of unit.querySelectorAll('[data-bundle-option-select]')) {
      if (!(select instanceof HTMLSelectElement)) continue;
      selection[Number(select.dataset.optionPosition)] = select.value;
    }
    return selection;
  }

  /** @param {HTMLElement} tier */
  #getTierSelections(tier) {
    /** @type {Record<number, Record<number, string>>} */
    const result = {};
    for (const unit of tier.querySelectorAll('[data-bundle-unit]')) {
      if (!(unit instanceof HTMLElement)) continue;
      result[Number(unit.dataset.bundleUnit)] = this.#getUnitSelection(unit);
    }
    return result;
  }

  #subscriptionMode() {
    const mode = this.dataset.subscriptionMode;
    return mode === 'optional' || mode === 'required' ? mode : 'disabled';
  }

  #subscriptionRequired() {
    return this.#subscriptionMode() !== 'disabled' &&
      (this.#subscriptionMode() === 'required' || this.#catalog?.requiresSellingPlan === true);
  }

  #hasActiveSubscription() {
    if (this.#subscriptionMode() === 'disabled') return false;
    if (this.#subscriptionRequired()) return true;
    return this.#purchaseOption === 'subscription';
  }

  /** @param {HTMLElement} tier */
  #tierQuantities(tier) {
    const quantity = Number(tier.dataset.quantity) || 1;
    const freeUnits = Math.max(0, Math.min(quantity - 1, Number(tier.dataset.freeUnits) || 0));
    return { quantity, freeUnits, paidQuantity: quantity - freeUnits };
  }

  /** @param {any} variant @param {string} planId */
  #sellingPlanAllocation(variant, planId) {
    return Array.isArray(variant?.sellingPlanAllocations)
      ? variant.sellingPlanAllocations.find((allocation) => String(allocation.sellingPlanId) === String(planId))
      : undefined;
  }

  /** A plan must cover every paid unit, even when a bundle mixes variants. */
  #planAppliesToTier(tier, planId) {
    if (!(tier instanceof HTMLElement) || !this.#catalog) return false;
    const { paidQuantity } = this.#tierQuantities(tier);
    const selections = this.#getTierSelections(tier);
    for (let index = 1; index <= paidQuantity; index += 1) {
      const variant = this.#catalog.resolve(selections[index] || {});
      const allocation = this.#sellingPlanAllocation(variant, planId);
      if (!variant?.available || !allocation || !Number.isFinite(Number(allocation.price)) ||
          allocation.price == null || Number(allocation.price) < 0) return false;
    }
    return true;
  }

  #initializeSubscriptions() {
    if (!this.#catalog) return;
    const configuration = `${this.#subscriptionMode()}:${this.dataset.subscriptionDefault || 'one-time'}`;
    if (configuration === this.#subscriptionConfiguration) return;
    this.#subscriptionConfiguration = configuration;
    this.#purchaseOption = this.#subscriptionRequired() ||
      (this.#catalog.sellingPlans.length > 0 && this.dataset.subscriptionDefault === 'subscription')
      ? 'subscription' : 'one-time';
    const tier = this.querySelector('[data-bundle-tier]:has([data-bundle-tier-radio]:checked)');
    const plan = this.#catalog.sellingPlans.find((candidate) => this.#planAppliesToTier(tier, String(candidate.id)));
    this.#sellingPlanId = plan ? String(plan.id) : '';
  }

  /**
   * Calculate a purchase choice without changing the selected option.
   * Subscription allocations are authoritative: a tier percentage is not a second subscription discount.
   * @param {HTMLElement} tier
   * @param {boolean} subscription
   */
  #tierPricing(tier, subscription = this.#hasActiveSubscription()) {
    const { quantity, freeUnits, paidQuantity } = this.#tierQuantities(tier);
    const discount = Number(tier.dataset.discount) || 0;
    const selections = this.#getTierSelections(tier);
    const plan = this.#catalog?.sellingPlans.find((candidate) => String(candidate.id) === this.#sellingPlanId);
    let saleTotal = 0;
    let compareTotal = 0;
    let paidTotal = 0;
    let subscriptionReferenceTotal = 0;
    let subscriptionReferenceAvailable = Boolean(plan);
    let allAvailable = true;
    let error = '';

    if (subscription && !plan) {
      error = this.#catalog?.sellingPlans.length
        ? 'Choose an available delivery plan to continue.'
        : 'Subscriptions are currently unavailable for this product.';
    }

    for (let index = 1; index <= quantity; index += 1) {
      const variant = this.#catalog?.resolve(selections[index] || {});
      if (!variant?.available) allAvailable = false;
      if (!variant) {
        subscriptionReferenceAvailable = false;
        continue;
      }
      const price = Number(variant.price) || 0;
      const requiresPlan = this.#catalog?.requiresSellingPlan === true || variant.requiresSellingPlan === true;
      saleTotal += price;
      compareTotal += Math.max(Number(variant.compareAtPrice) || 0, price);

      const allocation = subscription ? this.#sellingPlanAllocation(variant, this.#sellingPlanId) : undefined;
      const allocationPrice = allocation?.price == null ? NaN : Number(allocation.price);
      if (subscription) {
        // Compare the entire tier at the selected plan's unit prices. Gifts
        // still use ordinary cart lines; this reference does not affect them.
        if (!allocation || !Number.isFinite(allocationPrice) || allocationPrice < 0) {
          subscriptionReferenceAvailable = false;
        } else {
          subscriptionReferenceTotal += allocationPrice;
        }
      }

      if (subscription && index <= paidQuantity) {
        if (!allocation || !Number.isFinite(allocationPrice) || allocationPrice < 0) {
          error ||= 'This delivery plan is unavailable for a selected option. Choose another plan or product option.';
          paidTotal += price;
        } else {
          paidTotal += allocationPrice;
        }
      } else {
        if (index <= paidQuantity) paidTotal += price;
        if (this.#subscriptionMode() !== 'disabled' && requiresPlan) {
          error ||= subscription
            ? 'Free units cannot be added because this product is only available with a subscription.'
            : 'A selected product option is only available with a subscription.';
        }
      }
    }

    let finalTotal = saleTotal;
    let originalTotal = null;
    if (subscription) {
      finalTotal = paidTotal;
      if (subscriptionReferenceAvailable && subscriptionReferenceTotal > finalTotal) {
        originalTotal = subscriptionReferenceTotal;
      }
    } else if (freeUnits > 0) {
      finalTotal = paidTotal;
      originalTotal = saleTotal;
    } else if (discount > 0) {
      finalTotal = Math.round(saleTotal * (1 - discount / 100));
      originalTotal = saleTotal;
    } else if (compareTotal > saleTotal) {
      originalTotal = compareTotal;
    }

    return {
      finalTotal, originalTotal, available: allAvailable && !error, error, paidQuantity, freeUnits,
      comparisonAvailable: !subscription || subscriptionReferenceAvailable,
    };
  }

  /** Replace only supported text tokens; merchant copy is never interpreted as HTML. */
  #subscriptionText(template, values) {
    return String(template).replace(/\[(plan|quantity|free|unit|amount|payments)\]/g, (token, name) =>
      Object.prototype.hasOwnProperty.call(values, name) ? String(values[name]) : token
    );
  }

  /** Unit wording is editable; quantities and prices still come from the selected tier. */
  #subscriptionUnit(wrapper, quantity) {
    return quantity === 1
      ? wrapper?.getAttribute('data-bundle-unit-singular') ?? 'unit'
      : wrapper?.getAttribute('data-bundle-unit-plural') ?? 'units';
  }

  /** Keep editor formatting, substituting live values only into text nodes. */
  #renderSubscriptionText(element, template, values) {
    if (!element.hasAttribute('data-bundle-rich-text')) {
      element.textContent = this.#subscriptionText(template, values);
      return;
    }
    const source = document.createElement('template');
    source.innerHTML = String(template);
    const fragment = document.createDocumentFragment();
    const allowedTags = new Set(['B', 'STRONG', 'I', 'EM', 'U', 'A', 'BR']);
    const append = (node, parent) => {
      if (node.nodeType === Node.TEXT_NODE) {
        parent.append(document.createTextNode(this.#subscriptionText(node.textContent || '', values)));
        return;
      }
      if (!(node instanceof Element) || ['SCRIPT', 'STYLE'].includes(node.tagName)) return;
      let target = parent;
      if (allowedTags.has(node.tagName)) {
        target = document.createElement(node.tagName.toLowerCase());
        if (node.tagName === 'A') {
          const href = node.getAttribute('href');
          try {
            if (href && ['http:', 'https:', 'mailto:', 'tel:'].includes(new URL(href, document.baseURI).protocol)) {
              target.setAttribute('href', href);
              if (node.getAttribute('target') === '_blank') {
                target.setAttribute('target', '_blank');
                target.setAttribute('rel', 'noopener noreferrer');
              }
            }
          } catch { /* Ignore invalid editor links. */ }
        }
        parent.append(target);
      }
      for (const child of node.childNodes) append(child, target);
    };
    for (const child of source.content.childNodes) append(child, fragment);
    element.replaceChildren(fragment);
  }

  /** Use the theme accordion's height/fade timing only on a purchase-option change. */
  #animateSubscriptionDetails() {
    for (const animation of this.#subscriptionAnimations.values()) animation.cancel();
    this.#subscriptionAnimations.clear();
    if (!this.#hasActiveSubscription() || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    for (const details of this.querySelectorAll('[data-bundle-subscription-details]')) {
      if (!(details instanceof HTMLElement) || details.closest('[hidden]') || typeof details.animate !== 'function') continue;
      const height = details.getBoundingClientRect().height;
      if (!height) continue;
      const style = getComputedStyle(details);
      const speed = style.getPropertyValue('--animation-speed-slow').trim() || '0.2s';
      const duration = Number.parseFloat(speed) * (speed.endsWith('ms') ? 1 : 1000);
      if (!Number.isFinite(duration) || duration <= 0) continue;
      const animation = details.animate([
        { blockSize: '0px', opacity: 0, marginBlockStart: '0px', overflow: 'clip' },
        { blockSize: `${height}px`, opacity: 1, marginBlockStart: style.marginBlockStart, overflow: 'clip' },
      ], {
        duration,
        easing: style.getPropertyValue('--animation-easing').trim() || 'ease-in-out',
      });
      this.#subscriptionAnimations.set(details, animation);
      const clear = () => {
        if (this.#subscriptionAnimations.get(details) === animation) this.#subscriptionAnimations.delete(details);
      };
      animation.finished.then(clear, clear);
    }
  }

  /** Only describe renewal prices when the complete allocation schedule is known. */
  #renewalDescription(tier, plan, element, render = false) {
    const adjustments = Array.isArray(plan?.priceAdjustments) ? plan.priceAdjustments : [];
    if (!adjustments.length || adjustments.length > 2) return '';
    const firstOrderCount = Number(adjustments[0].orderCount);
    const hasSecondPrice = adjustments.length === 2;
    if (hasSecondPrice && (!Number.isInteger(firstOrderCount) || firstOrderCount < 1)) return '';
    const recurring = adjustments[hasSecondPrice ? 1 : 0];
    if (recurring.orderCount != null) return '';
    const { paidQuantity } = this.#tierQuantities(tier);
    const selections = this.#getTierSelections(tier);
    let total = 0;
    for (let index = 1; index <= paidQuantity; index += 1) {
      const variant = this.#catalog?.resolve(selections[index] || {});
      const allocation = this.#sellingPlanAllocation(variant, String(plan.id));
      const prices = Array.isArray(allocation?.priceAdjustments)
        ? [...allocation.priceAdjustments].sort((a, b) => Number(a.position) - Number(b.position)) : [];
      const price = hasSecondPrice ? prices[1]?.price : allocation?.price;
      if (price == null || !Number.isFinite(Number(price)) || Number(price) < 0) return '';
      total += Number(price);
    }
    const amount = this.#formatMoney(total);
    const template = !hasSecondPrice
      ? element?.getAttribute('data-template') ?? 'Renewal total: [amount].'
      : firstOrderCount === 1
        ? element?.getAttribute('data-first-payment-template') ?? 'After your first payment, each renewal is [amount].'
        : element?.getAttribute('data-payments-template') ?? 'After your first [payments] payments, each renewal is [amount].';
    const values = {
      amount,
      payments: firstOrderCount,
      quantity: paidQuantity,
      unit: this.#subscriptionUnit(element?.closest('[data-bundle-subscriptions]'), paidQuantity),
    };
    if (render) this.#renderSubscriptionText(element, template, values);
    return this.#subscriptionText(template, values);
  }

  #syncSubscriptionUI() {
    const wrappers = this.querySelectorAll('[data-bundle-subscriptions]');
    const enabled = this.#subscriptionMode() !== 'disabled';
    const required = this.#subscriptionRequired();
    const hasPlans = Boolean(this.#catalog?.sellingPlans.length);
    const active = this.#hasActiveSubscription();
    const busy = this.#submissionPromise !== null;
    const tier = this.querySelector('[data-bundle-tier]:has([data-bundle-tier-radio]:checked)');
    const result = tier instanceof HTMLElement && this.#catalog ? this.#tierPricing(tier, active) : null;
    const plan = this.#catalog?.sellingPlans.find((candidate) => String(candidate.id) === this.#sellingPlanId);

    // The form value belongs to the bundle even when its optional presentation block is absent.
    const input = this.querySelector('[data-bundle-selling-plan-input]');
    if (input instanceof HTMLInputElement) {
      input.value = active && !result?.error ? this.#sellingPlanId : '';
      input.disabled = !enabled || !active || !input.value || Boolean(result?.error);
    }

    wrappers.forEach((wrapper, wrapperIndex) => {
      if (!(wrapper instanceof HTMLElement)) return;
      wrapper.hidden = !enabled || (!required && !hasPlans && !active);
      if (wrapper.hasAttribute('data-bundle-show-selector')) {
        wrapper.toggleAttribute('data-bundle-selector-hidden', required && wrapper.dataset.bundleShowSelector === 'false');
      }

      for (const radio of wrapper.querySelectorAll('[data-bundle-purchase-option]')) {
        if (!(radio instanceof HTMLInputElement)) continue;
        radio.name = `${this.id}-purchase-${wrapperIndex}`;
        radio.checked = radio.value === (active ? 'subscription' : 'one-time');
        radio.disabled = busy || !enabled || (radio.value === 'one-time' ? required : !hasPlans);
      }
      const oneTimeChoice = wrapper.querySelector('[data-bundle-purchase-choice="one-time"]');
      if (oneTimeChoice instanceof HTMLElement) oneTimeChoice.hidden = required;

      const select = wrapper.querySelector('[data-bundle-selling-plan]');
      if (select instanceof HTMLSelectElement) {
        const plans = this.#catalog?.sellingPlans || [];
        const options = Array.from(select.options);
        if (options.length !== plans.length || plans.some((candidate, index) =>
          options[index]?.value !== String(candidate.id) || options[index]?.textContent !== String(candidate.name || '')
        )) {
          select.replaceChildren(...plans.map((candidate) => {
            const option = document.createElement('option');
            option.value = String(candidate.id);
            option.textContent = String(candidate.name || '');
            return option;
          }));
        }
        for (const option of select.options) {
          const exists = this.#catalog?.sellingPlans.some((candidate) => String(candidate.id) === option.value);
          option.disabled = !exists || !this.#planAppliesToTier(tier, option.value);
        }
        select.value = this.#sellingPlanId;
        select.disabled = busy || !enabled || !active || !hasPlans;
        select.setAttribute('aria-invalid', String(Boolean(active && result?.error)));
      }
      const planField = wrapper.querySelector('[data-bundle-plan-field]');
      if (planField instanceof HTMLElement) {
        planField.hidden = !enabled || !active || !hasPlans ||
          (wrapper.dataset.bundleShowDeliverySelector === 'false' && !result?.error);
      }

      if (tier instanceof HTMLElement && this.#catalog && enabled) {
        for (const option of ['one-time', 'subscription']) {
          const price = this.#tierPricing(tier, option === 'subscription');
          const amount = wrapper.querySelector(`[data-bundle-purchase-price="${option}"]`);
          const compare = wrapper.querySelector(`[data-bundle-purchase-compare="${option}"]`);
          if (amount) amount.textContent = price.error ? '—' : this.#formatMoney(price.finalTotal);
          if (compare instanceof HTMLElement) {
            compare.hidden = Boolean(price.error) || !(price.originalTotal > price.finalTotal);
            compare.textContent = compare.hidden ? '' : this.#formatMoney(price.originalTotal);
          }
        }
      }
      const details = wrapper.querySelector('[data-bundle-subscription-details]');
      if (details instanceof HTMLElement) details.hidden = !enabled || !active;
      const summary = wrapper.querySelector('[data-bundle-subscription-summary]');
      if (summary instanceof HTMLElement) summary.hidden = !enabled || !active || Boolean(result?.error);

      const firstOrder = wrapper.querySelector('[data-bundle-subscription-first-order]');
      if (firstOrder instanceof HTMLElement) {
        const visible = enabled && active && result && !result.error;
        if (visible) {
          const quantity = result.paidQuantity + result.freeUnits;
          const template = firstOrder.getAttribute(result.freeUnits > 0 ? 'data-template' : 'data-no-gifts-template') || '';
          this.#renderSubscriptionText(firstOrder, template, {
            quantity,
            free: result.freeUnits,
            unit: this.#subscriptionUnit(wrapper, quantity),
            amount: this.#formatMoney(result.finalTotal),
          });
        } else {
          firstOrder.textContent = '';
        }
        firstOrder.hidden = !visible || !firstOrder.textContent;
      }
      const quantity = wrapper.querySelector('[data-bundle-subscription-quantity]');
      if (quantity) {
        this.#renderSubscriptionText(quantity, quantity.getAttribute('data-template') ?? '[quantity]', {
          quantity: result?.paidQuantity || 0,
        });
      }
      const gifts = wrapper.querySelector('[data-bundle-subscription-gifts]');
      if (gifts instanceof HTMLElement) gifts.hidden = !active || !result?.freeUnits;
      const planName = wrapper.querySelector('[data-bundle-subscription-plan-name]');
      if (planName) {
        this.#renderSubscriptionText(planName, planName.getAttribute('data-template') ?? '[plan]', {
          plan: plan?.name || '',
        });
      }
      const renewal = wrapper.querySelector('[data-bundle-subscription-renewal]');
      if (renewal instanceof HTMLElement) {
        const text = active && !result?.error && tier instanceof HTMLElement
          ? this.#renewalDescription(tier, plan, renewal, true) : '';
        if (!text) renewal.textContent = '';
        renewal.hidden = !renewal.textContent;
      }
      const error = wrapper.querySelector('[data-bundle-subscription-error]');
      if (error instanceof HTMLElement) {
        error.textContent = enabled ? result?.error || '' : '';
        error.hidden = !error.textContent;
      }
    });

    const fallbackError = this.querySelector('[data-bundle-subscription-fallback-error]');
    if (fallbackError instanceof HTMLElement) {
      fallbackError.textContent = enabled && !wrappers.length ? result?.error || '' : '';
      fallbackError.hidden = !fallbackError.textContent;
    }
  }

  /** @param {HTMLElement} tier */
  #refreshTier(tier) {
    if (!this.#catalog) return;
    const { quantity } = this.#tierQuantities(tier);
    for (let unitIndex = 1; unitIndex <= quantity; unitIndex += 1) {
      const unit = tier.querySelector(`[data-bundle-unit="${unitIndex}"]`);
      if (unit instanceof HTMLElement) this.#refreshUnitAvailability(unit, this.#catalog);
    }
    const { finalTotal, originalTotal, available, comparisonAvailable } = this.#tierPricing(tier);

    const finalElement = tier.querySelector('[data-bundle-price]');
    const compareElement = tier.querySelector('[data-bundle-compare]');
    if (finalElement) finalElement.textContent = this.#formatMoney(finalTotal);
    if (compareElement instanceof HTMLElement) {
      compareElement.hidden = originalTotal == null;
      compareElement.textContent = originalTotal == null ? '' : this.#formatMoney(originalTotal);
    }
    const savings = Math.max(0, (Number(originalTotal) || 0) - finalTotal);
    for (const badge of tier.querySelectorAll('[data-bundle-savings-template]')) {
      const template = badge.getAttribute('data-bundle-savings-template') || '';
      badge.hidden = !comparisonAvailable;
      badge.textContent = template.replace(/\[amount\]/g, () => this.#formatMoney(savings));
    }

    tier.dataset.bundleAvailable = String(available);
    tier.dataset.bundleFinalTotal = String(finalTotal);
    tier.dataset.bundleOriginalTotal = originalTotal == null ? '' : String(originalTotal);
    this.#refreshTierImage(tier);
  }

  /**
   * Use the sticky's single-src image contract. Selection state stays in this
   * controller; gallery/cart/sticky variant IDs continue to describe unit one.
   * @param {Element} tier
   */
  #refreshTierImage(tier) {
    if (!this.#catalog || !(tier instanceof HTMLElement) ||
        tier.closest('bundle-quantity') !== this || tier.dataset.bundleImageBehavior !== 'last_selected_variant') return;
    const media = tier.querySelector('[data-bundle-tier-media]');
    if (!(media instanceof HTMLElement)) return;

    const tierKey = tier.querySelector('[data-bundle-tier-radio]')?.getAttribute('value');
    const unitIndex = this.#tierImageUnits.get(tierKey) || 1;
    let unit = tier.querySelector(`[data-bundle-unit="${unitIndex}"]`);
    if (!unit && unitIndex !== 1) {
      // A tier can shrink in the editor. Never retain an index that no longer exists.
      this.#tierImageUnits.delete(tierKey);
      unit = tier.querySelector('[data-bundle-unit="1"]');
    }
    const variant = this.#catalog.resolve(unit instanceof HTMLElement ? this.#getUnitSelection(unit) : {});
    const source = variant?.tierImageUrl || this.#catalog.tierImageFallbackUrl;
    if (!source) return;

    let image = media.querySelector('img');
    if (!image) {
      image = document.createElement('img');
      image.alt = '';
      image.loading = 'lazy';
      image.width = 320;
      image.height = 320;
      media.replaceChildren(image);
    }
    // Liquid supplies this contract initially; enforce it after editor DOM morphs too.
    if (image.hasAttribute('srcset')) image.removeAttribute('srcset');
    if (image.hasAttribute('sizes')) image.removeAttribute('sizes');
    if (image.getAttribute('src') !== source) image.setAttribute('src', source);
  }

  /** @param {HTMLElement} upsell */
  #upsellQuantity(upsell) {
    return Math.max(1, Math.min(10, Math.floor(Number(upsell.dataset.upsellQuantity) || 1)));
  }

  /** @param {HTMLElement} upsell */
  #upsellUnits(upsell) {
    return Array.from(upsell.querySelectorAll('[data-bundle-upsell-unit]')).filter((unit) =>
      unit instanceof HTMLElement && unit.closest('[data-bundle-upsell]') === upsell
    ).slice(0, this.#upsellQuantity(upsell));
  }

  /** @param {HTMLElement} upsell */
  #refreshUpsell(upsell) {
    const catalog = this.#getUpsellCatalog(upsell);
    if (!(catalog instanceof VariantCatalog)) {
      upsell.dataset.bundleAvailable = 'false';
      return;
    }
    const units = this.#upsellUnits(upsell);
    const offerFree = upsell.dataset.bundleOfferFree === 'true';
    const discount = offerFree ? 100 : Number(upsell.dataset.discount) || 0;
    let available = units.length === this.#upsellQuantity(upsell);
    let finalPrice = 0;
    let comparePrice = 0;
    for (const unit of units) {
      this.#refreshUnitAvailability(unit, catalog);
      const variant = catalog.resolve(this.#getUnitSelection(unit));
      if (!variant?.available) available = false;
      finalPrice += variant ? Math.round(Number(variant.price) * (1 - discount / 100)) : 0;
      comparePrice += upsell.dataset.upsellComparePriceSource === 'compare_at_price' && Number(variant?.compareAtPrice) > 0
        ? Number(variant.compareAtPrice)
        : Number(variant?.price) || 0;
    }

    const priceElement = upsell.querySelector('[data-bundle-upsell-price]');
    const compareElement = upsell.querySelector('[data-bundle-upsell-compare]');
    if (priceElement) priceElement.textContent = offerFree
      ? upsell.dataset.bundleFreeLabel || 'Free'
      : this.#formatMoney(finalPrice);
    if (compareElement instanceof HTMLElement) {
      compareElement.hidden = !(units.length > 0 && discount > 0);
      compareElement.textContent = units.length > 0 && discount > 0 ? this.#formatMoney(comparePrice) : '';
    }

    upsell.dataset.bundleAvailable = String(available);
    upsell.dataset.bundleFinalTotal = String(finalPrice);
    upsell.dataset.bundleOriginalTotal = String(discount > 0 ? comparePrice : finalPrice);
  }

  /** @param {HTMLElement} unit @param {VariantCatalog} catalog */
  #reconcileUnitSelection(unit, catalog) {
    const selection = catalog.reconcile(this.#getUnitSelection(unit));
    if (!selection) return;
    for (const [position, value] of Object.entries(selection)) {
      this.#setUnitOption(unit, Number(position), value, false, 0, false);
    }
  }

  /** @param {HTMLElement} unit @param {VariantCatalog} catalog */
  #refreshUnitAvailability(unit, catalog) {
    this.#reconcileUnitSelection(unit, catalog);
    const selection = this.#getUnitSelection(unit);

    for (const option of unit.querySelectorAll('[data-bundle-option]')) {
      if (!(option instanceof HTMLElement)) continue;
      const position = Number(option.dataset.optionPosition);
      const select = option.querySelector('[data-bundle-option-select]');
      if (!(select instanceof HTMLSelectElement)) continue;

      for (const selectOption of Array.from(select.options)) {
        const available = catalog.isCandidateAvailable(selection, position, selectOption.value);
        selectOption.disabled = !available && selectOption.value !== select.value;
      }

      for (const input of option.querySelectorAll('[data-bundle-option-swatch]')) {
        if (!(input instanceof HTMLInputElement)) continue;
        const available = catalog.isCandidateAvailable(selection, position, input.value);
        input.disabled = !available && input.value !== select.value;
        input.dataset.optionAvailable = String(available);
        input.setAttribute('aria-disabled', String(!available));
      }
    }
  }

  #refreshAll() {
    const activeTier = this.querySelector('[data-bundle-tier]:has([data-bundle-tier-radio]:checked)');
    if (this.#subscriptionMode() !== 'disabled') {
      for (const tier of this.querySelectorAll('[data-bundle-tier]')) {
        if (tier instanceof HTMLElement) this.#refreshTier(tier);
      }
    } else if (activeTier instanceof HTMLElement) {
      this.#refreshTier(activeTier);
    }

    this.#initializeUpsells();
    this.#updateButtonState();
  }

  #isBundleAvailable() {
    const activeTier = this.querySelector('[data-bundle-tier]:has([data-bundle-tier-radio]:checked)');
    let available = activeTier instanceof HTMLElement && activeTier.dataset.bundleAvailable === 'true';

    for (const upsell of this.querySelectorAll('[data-bundle-upsell]')) {
      if (!(upsell instanceof HTMLElement) || !this.#upsellAppliesToTier(upsell)) continue;
      const checkbox = upsell.querySelector('[data-bundle-upsell-check]');
      if (checkbox instanceof HTMLInputElement && checkbox.checked && upsell.dataset.bundleAvailable !== 'true') {
        available = false;
      }
    }

    return available;
  }

  #updateButtonState() {
    this.#syncSubscriptionUI();
    const available = this.#catalog !== null && this.#isBundleAvailable();
    const busy = this.#submissionPromise !== null;
    for (const button of this.querySelectorAll('[data-bundle-buy-buttons] button[name="add"], [data-bundle-buy-now]')) {
      if (!(button instanceof HTMLButtonElement)) continue;
      button.disabled = busy || !available;
      if (busy) button.setAttribute('aria-busy', 'true');
      else button.removeAttribute('aria-busy');
    }
    const totals = this.#selectedTotals();
    const price = totals ? this.#formatMoney(totals.finalTotal) : '';
    for (const element of this.querySelectorAll('[data-bundle-total-price]')) {
      if (!(element instanceof HTMLElement) || element.closest('bundle-quantity') !== this) continue;
      if (element.textContent !== price) element.textContent = price;
      element.hidden = !price;
    }
    this.dispatchEvent(new CustomEvent('bundle:state-change', { bubbles: true }));
  }

  /** One total shared by native/custom purchase buttons and the sticky state. */
  #selectedTotals() {
    const tier = this.querySelector('[data-bundle-tier]:has([data-bundle-tier-radio]:checked)');
    if (!(tier instanceof HTMLElement)) return null;
    let finalTotal = Number(tier.dataset.bundleFinalTotal) || 0;
    let originalTotal = Math.max(finalTotal, Number(tier.dataset.bundleOriginalTotal) || 0);
    for (const upsell of this.querySelectorAll('[data-bundle-upsell]')) {
      if (!(upsell instanceof HTMLElement) || !this.#upsellAppliesToTier(upsell)) continue;
      const checkbox = upsell.querySelector('[data-bundle-upsell-check]');
      if (!(checkbox instanceof HTMLInputElement) || !checkbox.checked) continue;
      const price = Number(upsell.dataset.bundleFinalTotal) || 0;
      finalTotal += price;
      originalTotal += Math.max(price, Number(upsell.dataset.bundleOriginalTotal) || 0);
    }
    return { finalTotal, originalTotal };
  }

  /** @param {HTMLElement} tier */
  #syncGallery(tier) {
    if (!this.#catalog) return;
    const unitOne = tier.querySelector('[data-bundle-unit="1"]');
    if (!(unitOne instanceof HTMLElement)) return;
    const variant = this.#catalog.resolve(this.#getUnitSelection(unitOne));
    const mediaId = variant?.featuredMediaId;
    if (!mediaId) return;

    const scope = this.closest('#quick-add-modal-content') || this.closest('.shopify-section');
    const gallery = scope?.querySelector('media-gallery');
    if (!gallery) return;

    if (typeof gallery.selectMediaById === 'function') {
      gallery.selectMediaById(String(mediaId));
      return;
    }

    import('@theme/media-gallery')
      .then(() => customElements.whenDefined('media-gallery'))
      .then(() => {
        if (typeof gallery.selectMediaById === 'function') gallery.selectMediaById(String(mediaId));
      })
      .catch((error) => console.warn('[bundle-quantity] Media gallery sync failed', error));
  }

  /**
   * Public state consumed by the sticky button and cart Edit.
   * variantId/variantAvailable describe unit one only; available describes the
   * complete pack (including selected upsells). busy describes a pending cart add.
   */
  getBundleState() {
    if (!this.#catalog) return null;
    const tier = this.querySelector('[data-bundle-tier]:has([data-bundle-tier-radio]:checked)');
    if (!(tier instanceof HTMLElement)) return null;

    const quantity = Number(tier.dataset.quantity) || 1;
    const unitOne = tier.querySelector('[data-bundle-unit="1"]');
    const unitOneSelection = unitOne instanceof HTMLElement ? this.#getUnitSelection(unitOne) : {};
    const variant = this.#catalog.resolve(unitOneSelection);
    const { finalTotal, originalTotal } = this.#selectedTotals() || { finalTotal: 0, originalTotal: 0 };
    const tierLabel = tier.querySelector('.bundle-tier__title')?.textContent?.trim() || '';
    const variantTitle = Array.isArray(variant?.options) && this.#catalog.optionCount > 0 ? variant.options.join(' / ') : '';

    return {
      available: this.#isBundleAvailable(),
      variantId: variant?.id == null ? '' : String(variant.id),
      variantAvailable: Boolean(variant?.available),
      busy: this.#submissionPromise !== null,
      quantity,
      tierLabel,
      variantTitle,
      singleVariant: this.#catalog.variants.length === 1,
      price: this.#formatMoney(finalTotal),
      comparePrice: originalTotal > finalTotal ? this.#formatMoney(originalTotal) : '',
      imageUrl: variant?.featuredImageUrl || '',
    };
  }

  /** Public entry point used by Horizon's existing sticky Add to Cart. */
  addToCart() {
    return this.#addToCart();
  }

  async #buyNow() {
    if (this.#submissionPromise) return;
    const succeeded = await this.#addToCart({ checkout: true });
    if (!succeeded) return;

    const root = this.dataset.rootUrl || '/';
    const checkoutPath = `${root.endsWith('/') ? root : `${root}/`}checkout`;
    try {
      window.location.assign(checkoutPath);
    } catch (error) {
      // The cart add is already confirmed. A retry must only navigate again.
      console.warn('[bundle-quantity] Checkout navigation failed', error);
    }
  }

  #collectLineItemProperties() {
    const details = this.closest('.product-details') || this.closest('#quick-add-modal-content');
    if (!(details instanceof HTMLElement)) return {};

    /** @type {Record<string, string>} */
    const properties = {};
    const fields = details.querySelectorAll(
      'product-custom-property-component input[name^="properties["], product-custom-property-component textarea[name^="properties["]'
    );

    for (const field of fields) {
      if (!(field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement) || field.disabled) continue;
      if (!field.checkValidity()) {
        field.reportValidity();
        return null;
      }
      if (field instanceof HTMLInputElement && (field.type === 'checkbox' || field.type === 'radio') && !field.checked) continue;

      const match = field.name.match(/^properties\[(.+)\]$/);
      const key = match?.[1]?.trim();
      if (!key) continue;
      const value = field.value;
      if (value === '' && !field.required) continue;
      properties[key] = value;
    }

    return properties;
  }

  #collectItems() {
    if (!this.#catalog) return null;
    const activeTier = this.querySelector('[data-bundle-tier]:has([data-bundle-tier-radio]:checked)');
    if (!(activeTier instanceof HTMLElement)) return null;
    const subscription = this.#hasActiveSubscription();
    if (this.#subscriptionMode() !== 'disabled' && !this.#tierPricing(activeTier, subscription).available) {
      this.#refreshTier(activeTier);
      this.#updateButtonState();
      return null;
    }

    const mainProperties = this.#collectLineItemProperties();
    if (mainProperties === null) return null;
    const hasMainProperties = Object.keys(mainProperties).length > 0;

    /** @type {Map<string, {variantId: string, quantity: number, sellingPlanId?: string, properties?: Record<string, string>}>} */
    const items = new Map();
    const addItem = (variantId, properties, sellingPlanId) => {
      const normalizedProperties = properties && Object.keys(properties).length > 0 ? properties : undefined;
      const key = `${variantId}${OPTION_SEPARATOR}${sellingPlanId || ''}${OPTION_SEPARATOR}${normalizedProperties ? JSON.stringify(normalizedProperties) : ''}`;
      const existing = items.get(key);
      if (existing) {
        existing.quantity += 1;
      } else {
        items.set(key, {
          variantId, quantity: 1,
          ...(sellingPlanId ? { sellingPlanId } : {}),
          ...(normalizedProperties ? { properties: normalizedProperties } : {}),
        });
      }
    };

    const { quantity, paidQuantity } = this.#tierQuantities(activeTier);
    const selections = this.#getTierSelections(activeTier);

    for (let unitIndex = 1; unitIndex <= quantity; unitIndex += 1) {
      const variant = this.#catalog.resolve(selections[unitIndex] || {});
      if (!variant?.available) return null;
      addItem(
        String(variant.id),
        hasMainProperties ? mainProperties : undefined,
        subscription && unitIndex <= paidQuantity ? this.#sellingPlanId : undefined
      );
    }

    for (const upsell of this.querySelectorAll('[data-bundle-upsell]')) {
      if (!(upsell instanceof HTMLElement) || !this.#upsellAppliesToTier(upsell)) continue;
      const checkbox = upsell.querySelector('[data-bundle-upsell-check]');
      if (!(checkbox instanceof HTMLInputElement) || !checkbox.checked) continue;
      const catalog = this.#getUpsellCatalog(upsell);
      const units = this.#upsellUnits(upsell);
      if (!(catalog instanceof VariantCatalog) || units.length !== this.#upsellQuantity(upsell)) return null;
      for (const unit of units) {
        const variant = catalog.resolve(this.#getUnitSelection(unit));
        if (!variant?.available) return null;
        addItem(String(variant.id));
      }
    }

    return Array.from(items.values());
  }

  /**
   * Compare submitted contents, independent of selector or property ordering.
   * @param {Array<{variantId: string, quantity: number, sellingPlanId?: string, properties?: Record<string, string>}>} items
   */
  #checkoutSignature(items) {
    return JSON.stringify(items.map((item) => [
      item.variantId,
      item.quantity,
      item.sellingPlanId || '',
      Object.entries(item.properties || {}).sort(([left], [right]) => left.localeCompare(right)),
    ]).sort((left, right) => JSON.stringify(left).localeCompare(JSON.stringify(right))));
  }

  #addToCart({ checkout = false } = {}) {
    // new-horizon: share one transaction across the form, sticky and Buy now.
    // Claim it before awaiting custom-element upgrades or sending cart requests.
    if (this.#submissionPromise) return this.#submissionPromise;
    const items = this.#collectItems();
    if (!items?.length) return Promise.resolve(false);

    const signature = checkout ? this.#checkoutSignature(items) : '';
    if (checkout && this.#confirmedCheckout?.signature === signature) {
      // The same Buy now already succeeded. Never repeat its cart mutation just
      // because checkout is slow, was interrupted, or another click arrived.
      return Promise.resolve(true);
    }

    const productForm = this.querySelector('product-form-component');
    if (!productForm) return Promise.resolve(false);

    // Native ATC remains a new addition. Changed Buy now contents are new too.
    this.#confirmedCheckout = null;

    this.#submissionPromise = Promise.resolve()
      .then(async () => {
        await customElements.whenDefined('product-form-component');
        if (typeof productForm.addItems !== 'function') return false;

        const result = await productForm.addItems(
          items,
          checkout ? { broadcast: false, includeSections: false, refreshCart: false } : undefined
        );
        const succeeded = result?.detail?.didError === false;
        // Save the exact submitted payload before releasing the shared lock.
        // A selector change while awaiting the response cannot change it.
        if (succeeded && checkout) this.#confirmedCheckout = { signature };
        return succeeded;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[bundle-quantity] Add to cart failed', error);
        return false;
      })
      .finally(() => {
        this.#submissionPromise = null;
        this.#updateButtonState();
      });

    this.#updateButtonState();
    return this.#submissionPromise;
  }

  /**
   * Derive the active market's money presentation from Shopify-rendered Liquid.
   * The USD argument below supplies only the numeric scale/precision to the
   * existing formatter; all currency text comes from the rendered sample.
   * @param {string} sample
   * @returns {((cents: number) => string) | null}
   */
  #sampleMoneyFormatter(sample) {
    let displaySample = sample;
    if (/&(?:#\d+|#x[\da-f]+|[a-z]+);/i.test(displaySample)) {
      const decoder = document.createElement('textarea');
      decoder.innerHTML = displaySample;
      displaySample = decoder.value;
    }
    const normalizedSample = displaySample.replace(/[\u00a0\u202f\u2007]/g, ' ').replace(/’/g, "'");
    const tokens = [
      'amount', 'amount_no_decimals', 'amount_with_comma_separator',
      'amount_no_decimals_with_comma_separator', 'amount_no_decimals_with_space_separator',
      'amount_with_space_separator', 'amount_with_period_and_space_separator', 'amount_with_apostrophe_separator',
    ];
    for (const token of tokens) {
      const pattern = `{{${token}}}`;
      const probe = formatMoney(123456789, pattern, 'USD');
      const index = normalizedSample.indexOf(probe);
      if (index < 0 || /\d/.test(normalizedSample[index - 1] || '') ||
          /\d/.test(normalizedSample[index + probe.length] || '')) continue;
      const numericSample = displaySample.slice(index, index + probe.length);
      const spaceSeparator = numericSample.match(/[\u00a0\u202f\u2007]/)?.[0] || ' ';
      const apostropheSeparator = numericSample.includes('’') ? '’' : "'";
      const prefix = displaySample.slice(0, index);
      const suffix = displaySample.slice(index + probe.length);
      return (cents) => prefix + formatMoney(cents, pattern, 'USD')
        .replace(/ /g, spaceSeparator).replace(/'/g, apostropheSeparator) + suffix;
    }
    return null;
  }

  /** @param {number} cents */
  #formatMoney(cents) {
    const showCurrency = this.dataset.showCurrency === 'true';
    const sample = showCurrency ? this.dataset.moneyWithCurrencySample : this.dataset.moneySample;
    const value = Number(cents) || 0;
    if (sample) {
      const currency = this.dataset.currency || 'USD';
      const locale = this.dataset.locale || document.documentElement.lang || 'en';
      const key = JSON.stringify([sample, currency, locale, showCurrency]);
      if (key !== this.#moneyFormatterKey) {
        this.#moneyFormatterKey = key;
        this.#moneyFormatter = this.#sampleMoneyFormatter(sample);
        if (!this.#moneyFormatter) {
          try {
            const formatter = new Intl.NumberFormat(locale, {
              style: 'currency', currency, currencyDisplay: showCurrency ? 'code' : 'symbol',
            });
            this.#moneyFormatter = (amount) => formatter.format(amount / 100);
          } catch {
            this.#moneyFormatter = null;
          }
        }
      }
      if (this.#moneyFormatter) return this.#moneyFormatter(value);
    }
    const format = showCurrency ? this.dataset.moneyWithCurrencyFormat : this.dataset.moneyFormat;
    return formatMoney(value, format || '{{amount}}', this.dataset.currency || 'USD');
  }
}

if (!customElements.get('bundle-quantity')) {
  customElements.define('bundle-quantity', BundleQuantity);
}
